import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { stripeService } from '@/services/stripe';
import { buildStripeGooglePayRequest } from '@/utils/google';
import { buildAdyenGooglePayPaymentRequest } from '@/utils/adyen';
import { Errors } from '@/constants/errors';
import type {
  AdyenGooglePayPaymentParams,
  ChargebeeWalletPaymentParams,
  GooglePayPaymentParams,
  StripeDirectGooglePayParams,
  StripeExpressCheckoutParams,
  NuveiWalletPaymentParams,
} from '@/types/paymentHandlers';
import { finalizeChargebeeWalletPayment } from '@/payment/chargebeePaymentHandler';
import type { AdyenPaymentResponse, StripePaymentResponse, NuveiPaymentResponse } from '@/types/payment';
import { InfoLogs } from '@/constants/logs';
import { logError, logInfo } from '@/utils/logger';
import { stripeGooglePayAuthMethod } from '@/utils/googlePayAuthMethod';
import { buildNuveiWalletPaymentRequest } from '@/utils/nuvei';

// Stripe-specific Google Pay handler
const handleStripeGooglePay = async (
  _checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: StripeExpressCheckoutParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<StripePaymentResponse | null> => {
  const { event, elements, stripe } = params;

  const billingDetails = {
    ...event.billingDetails,
    address: {
      ...event.billingDetails?.address,
      line2: event.billingDetails?.address?.line2 || '',
    },
  };

  // `stripe` is the instance that owns `elements` — see
  // `StripeExpressCheckoutParams.stripe` for why the shared slot is not enough.
  const googlePayResults = await stripeService.createGooglePayPaymentMethod(elements, billingDetails, stripe);
  if (googlePayResults) {
    logInfo(InfoLogs.googlePayAuthorized, undefined, {
      payment_method: PaymentMethodEnum.GOOGLE_PAY,
      payment_provider: ProviderEnum.STRIPE,
      gpay_auth_method: stripeGooglePayAuthMethod(googlePayResults),
    });
    return (await sendPaymentToServer(
      buildStripeGooglePayRequest(googlePayResults, providerSettings, event.billingDetails!, params.zipCode)
    )) as StripePaymentResponse;
  }
  return null;
};

// Direct pay.js flow (`checkout_stripe_direct_google_pay`): Google already
// returned a Stripe Token from our own page — no Express Checkout Element
// involved. Convert it to a PaymentMethod and ride the same finalize request
// as the ECE flow so the BE contract is unchanged.
const isStripeDirectGooglePayParams = (params: GooglePayPaymentParams): params is StripeDirectGooglePayParams =>
  'googlePayTokenId' in params;

const handleStripeDirectGooglePay = async (
  _checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: StripeDirectGooglePayParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<StripePaymentResponse | null> => {
  const paymentMethod = await stripeService.createGooglePayPaymentMethodFromToken(
    params.googlePayTokenId,
    params.billingDetails,
    params.stripe
  );
  logInfo(InfoLogs.googlePayAuthorized, undefined, {
    payment_method: PaymentMethodEnum.GOOGLE_PAY,
    payment_provider: ProviderEnum.STRIPE,
    gpay_auth_method: stripeGooglePayAuthMethod(paymentMethod),
  });
  return (await sendPaymentToServer(
    buildStripeGooglePayRequest(paymentMethod, providerSettings, params.billingDetails, params.zipCode)
  )) as StripePaymentResponse;
};

// Adyen-specific Google Pay handler
const handleAdyenGooglePay = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: AdyenGooglePayPaymentParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<AdyenPaymentResponse> => {
  const request = buildAdyenGooglePayPaymentRequest(params, providerSettings, checkout.customer);
  return (await sendPaymentToServer(request)) as AdyenPaymentResponse;
};

const handleNuveiGooglePay = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: NuveiWalletPaymentParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<NuveiPaymentResponse> => {
  const request = buildNuveiWalletPaymentRequest({
    paymentMethodId: providerSettings.id || '',
    paymentMethodDisplayName: providerSettings.paymentMethodDisplayName || 'Google Pay',
    // Sheet-collected email wins — same precedence as the Adyen wallet handlers.
    email: params.email || checkout.customer.email || '',
    sessionToken: params.sessionToken,
    walletParams: { transactionId: params.transactionId },
  });
  return (await sendPaymentToServer(request)) as NuveiPaymentResponse;
};

export const handleGooglePayPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params?: GooglePayPaymentParams
): Promise<StripePaymentResponse | AdyenPaymentResponse | NuveiPaymentResponse | null> => {
  if (!params) {
    throw new Error('Google Pay payment requires parameters');
  }

  const providerSettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.GOOGLE_PAY
  );

  if (!providerSettings) {
    throw new Error('Google Pay payment method settings not found');
  }

  try {
    switch (providerSettings.provider) {
      case ProviderEnum.STRIPE:
        if (isStripeDirectGooglePayParams(params)) {
          return await handleStripeDirectGooglePay(checkout, sendPaymentToServer, params, providerSettings);
        }
        return (await handleStripeGooglePay(
          checkout,
          sendPaymentToServer,
          params as StripeExpressCheckoutParams,
          providerSettings
        )) as StripePaymentResponse;
      case ProviderEnum.ADYEN:
        return await handleAdyenGooglePay(
          checkout,
          sendPaymentToServer,
          params as AdyenGooglePayPaymentParams,
          providerSettings
        );
      case ProviderEnum.NUVEI:
        return await handleNuveiGooglePay(
          checkout,
          sendPaymentToServer,
          params as NuveiWalletPaymentParams,
          providerSettings
        );
      // Subscriptions: Chargebee's mounted button already ran the sheet and
      // authorized the intent — only the server-side finalize remains.
      case ProviderEnum.CHARGEBEE:
        return (await finalizeChargebeeWalletPayment(
          checkout,
          sendPaymentToServer,
          (params as ChargebeeWalletPaymentParams).paymentIntent
        )) as StripePaymentResponse;
      default:
        throw new Error(`Unsupported provider for Google Pay: ${providerSettings.provider}`);
    }
  } catch (error: unknown) {
    logError(Errors.google.errorWhileSendingPayment, error);
    throw error;
  }
};
